import { gql } from "@apollo/client";
import { DEFAULT_USER_SETTINGS, UserSettings } from "@hex/common";
import { useCallback } from "react";

import { TYPEAHEAD_COMPLETION_PROVIDER } from "../components/cell/monaco/TypeaheadCompletionProvider.js";

import { useUserForSettings } from "./me/useUserForSettings";
import { useSetUserSettingsMutation } from "./useUpdateUserSettings.generated";

gql`
  mutation setUserSettings($settings: UserSettings!) {
    updateUserSettings(settings: $settings) {
      id
      userSettings
    }
  }
`;

export const useUpdateUserSettings = (): {
  updateWordWrapMarkdown: (enabled: boolean) => Promise<void>;
  updateWordWrapCode: (enabled: boolean) => Promise<void>;
  updateWordWrapSql: (enabled: boolean) => Promise<void>;
  updateLineNumbersMarkdown: (enabled: boolean) => Promise<void>;
  updateLineNumbersSql: (enabled: boolean) => Promise<void>;
  updateLineNumbersCode: (enabled: boolean) => Promise<void>;
  updateRelativeLineNumbers: (enabled: boolean) => Promise<void>;
  updateMagicTypeaheadCode: (enabled: boolean) => Promise<void>;
  updateMagicTypeaheadAndTos: (
    tosAccepted: boolean,
    typeaheadEnabled: boolean,
  ) => Promise<void>;
  updateDefaultAgentDataAccess: (option: "all" | "endorsed") => Promise<void>;
} => {
  const { id, userSettings } = useUserForSettings();
  const [setUserSettings] = useSetUserSettingsMutation();

  const updateUserSettings = useCallback(
    async (newSettings: UserSettings) => {
      if (id == null) return;
      await setUserSettings({
        variables: { settings: newSettings },
        optimisticResponse: {
          __typename: "Mutation",
          updateUserSettings: {
            id,
            __typename: "User",
            userSettings: newSettings,
          },
        },
      });
    },
    [id, setUserSettings],
  );

  const updateWordWrapMarkdown = useCallback(
    async (enabled: boolean) =>
      await updateUserSettings({
        ...userSettings,
        wordWrap: {
          ...userSettings.wordWrap,
          markdown: enabled,
        },
      }),
    [userSettings, updateUserSettings],
  );

  const updateWordWrapSql = useCallback(
    async (enabled: boolean) =>
      await updateUserSettings({
        ...userSettings,
        wordWrap: {
          ...userSettings.wordWrap,
          sql: enabled,
        },
      }),
    [userSettings, updateUserSettings],
  );

  const updateWordWrapCode = useCallback(
    async (enabled: boolean) =>
      await updateUserSettings({
        ...userSettings,
        wordWrap: {
          ...userSettings.wordWrap,
          code: enabled,
        },
      }),
    [userSettings, updateUserSettings],
  );

  const updateLineNumbersMarkdown = useCallback(
    async (enabled: boolean) =>
      await updateUserSettings({
        ...userSettings,
        lineNumbers: {
          ...userSettings.lineNumbers,
          markdown: enabled,
        },
      }),
    [userSettings, updateUserSettings],
  );

  const updateLineNumbersSql = useCallback(
    async (enabled: boolean) =>
      await updateUserSettings({
        ...userSettings,
        lineNumbers: {
          ...userSettings.lineNumbers,
          sql: enabled,
        },
      }),
    [userSettings, updateUserSettings],
  );

  const updateRelativeLineNumbers = useCallback(
    async (enabled: boolean) =>
      await updateUserSettings({
        ...userSettings,
        relativeLineNumbers: enabled,
      }),
    [userSettings, updateUserSettings],
  );

  const updateLineNumbersCode = useCallback(
    async (enabled: boolean) =>
      await updateUserSettings({
        ...userSettings,
        lineNumbers: {
          ...userSettings.lineNumbers,
          code: enabled,
        },
      }),
    [userSettings, updateUserSettings],
  );

  const updateMagicTypeaheadCode = useCallback(
    async (enabled: boolean) => {
      TYPEAHEAD_COMPLETION_PROVIDER.enableOrDisable(enabled);
      await updateUserSettings({
        ...userSettings,
        magicTypeahead: {
          ...(userSettings.magicTypeahead ??
            DEFAULT_USER_SETTINGS.magicTypeahead),
          code: enabled,
        },
      });
    },
    [userSettings, updateUserSettings],
  );

  const updateMagicTypeaheadAndTos = useCallback(
    async (tosAccepted: boolean, typeaheadEnabled: boolean) => {
      await updateUserSettings({
        ...userSettings,
        magicTypeahead: {
          ...userSettings.magicTypeahead,
          code: typeaheadEnabled,
          magicTosAcceptedJuly2024: tosAccepted,
        },
      });
    },
    [userSettings, updateUserSettings],
  );

  const updateDefaultAgentDataAccess = useCallback(
    async (option: "all" | "endorsed") => {
      await updateUserSettings({
        ...userSettings,
        defaultAgentDataAccess: option,
      });
    },
    [userSettings, updateUserSettings],
  );

  return {
    updateWordWrapMarkdown,
    updateWordWrapCode,
    updateWordWrapSql,
    updateLineNumbersMarkdown,
    updateLineNumbersSql,
    updateLineNumbersCode,
    updateRelativeLineNumbers,
    updateMagicTypeaheadCode,
    updateMagicTypeaheadAndTos,
    updateDefaultAgentDataAccess,
  };
};
